/**
 * Publish feature module (W2-09 publish-import; DESIGN.md §4.16, §5.11; WORKPACKAGES Appendix B: `file.export` and
 * `file.copyChartJson` of the `file.*` namespace, `publish.*`).
 *
 * Provides:
 *  - the Publish workspace panels: `publish.overview` (slot publish.left: check, compatibility, stats) and
 *    `publish.package` (slot publish.right: package details and export);
 *  - the Export dialog (`DIALOG.exportDialog`);
 *  - commands: `file.export` (`{ quick? }`, Ctrl+E opens the dialog in quick mode: Enter exports),
 *    `file.copyChartJson`, `publish.details` (`{ field? }`), `publish.focusField` (`{ field }`, a `meta.*` path:
 *    Problems "Go" through inspector.focusField), `publish.playPreview`, `publish.clampPreview`,
 *    `publish.rpeChecklist` (the RPE checklist bundle for Appendix C H6, rpeChecklist.ts);
 *  - Settings rows for the export preferences (`ext.publish.*`) and a help topic.
 */

import type { CommandArgs } from '../../app/registry/commands';
import type {
  CommandContext,
  CommandDef,
  FeatureModule,
  HelpTopicDef,
  KeyBinding,
  KeymapPresetId,
  SettingDef,
} from '../../app/registry/types';
import { DIALOG } from '../../app/dialogs/types';
import type { MessageRef } from '../../core/util/types';
import { runCopyChart } from './copyChart';
import { ExportDialog } from './ExportDialog';
import { currentOptions, downloadBytes } from './exportFlow';
import { buildRpeChecklist } from './rpeChecklist';
import { CLAMP_PREVIEW_LABEL, musicOffsetSec, patchMeta, playPreviewRange } from './PackageForm';
import { clampedPreview, songLengthSec } from './preflight';
import { focusFieldPath, isPackageFieldPath, publishSession, readPrefs, type FocusField } from './prefs';
import { PublishLeftPanel, PublishRightPanel } from './PublishWorkspace';
import { m } from './publish.messages';
import { prefSelect, prefSwitch } from './SettingRows';

/** Panel ids (the right panel hosts the package form that `publish.details` focuses). */
export const PUBLISH_PANELS = { overview: 'publish.overview', package: 'publish.package' } as const;

const FOCUS_FIELDS: readonly FocusField[] = ['name', 'level', 'charter', 'preview', 'tags'];

function noProject(ctx: CommandContext): true | MessageRef {
  return ctx.store !== null ? true : { key: m.reasonNoProject };
}

function quickArg(args: unknown): boolean {
  return typeof args === 'object' && args !== null && (args as { readonly quick?: unknown }).quick === true;
}

function fieldArg(args: unknown): FocusField {
  const f = typeof args === 'object' && args !== null ? (args as { readonly field?: unknown }).field : undefined;
  return typeof f === 'string' && (FOCUS_FIELDS as readonly string[]).includes(f) ? (f as FocusField) : 'name';
}

/** Palette choices of `publish.focusField`. */
const FOCUS_FIELD_CHOICES: ReadonlyArray<{ readonly field: string; readonly label: MessageRef }> = [
  { field: 'meta.name', label: { key: m.fieldName } },
  { field: 'meta.level', label: { key: m.fieldLevel } },
  { field: 'meta.difficulty', label: { key: m.fieldDifficulty } },
  { field: 'meta.charter', label: { key: m.fieldCharter } },
  { field: 'meta.composer', label: { key: m.fieldComposer } },
  { field: 'meta.illustrator', label: { key: m.fieldIllustrator } },
  { field: 'meta.previewStart', label: { key: m.fieldPreview } },
  { field: 'meta.tags', label: { key: m.fieldTags } },
  { field: 'meta.intro', label: { key: m.fieldIntro } },
  { field: 'meta.tip', label: { key: m.fieldTip } },
];

const focusFieldCommand: CommandDef<CommandArgs['publish.focusField']> = {
  id: 'publish.focusField',
  title: m.cmdFocusField,
  category: 'Publish',
  icon: 'TextCursorInput',
  level: 'guided',
  args: {
    kind: 'choice',
    label: { key: m.argField },
    options: FOCUS_FIELD_CHOICES.map((c) => ({ value: { field: c.field }, label: c.label })),
  },
  enabled: noProject,
  run(ctx, args) {
    // Typed, but the palette and keymap may hand anything over.
    const field: unknown = (args as { readonly field?: unknown } | null | undefined)?.field;
    ctx.ui.showPanel(PUBLISH_PANELS.package);
    // A metadata field without a control of its own: the PACKAGE panel takes the keyboard focus.
    if (typeof field === 'string' && isPackageFieldPath(field)) publishSession.requestFocus(field);
    else ctx.ui.focusRegion('publish.right');
  },
};

const exportCommand: CommandDef<CommandArgs['file.export']> = {
  id: 'file.export',
  title: m.cmdExport,
  category: 'File',
  icon: 'PackageCheck',
  level: 'guided',
  hint: 4,
  aliases: ['export', 'save as', 'pez', 'package', 'publish', 'download', 'zip', 'backup', '导出', '打包'],
  menus: [{ menu: 'app.project', group: 'file', order: 20 }],
  enabled: noProject,
  async run(ctx, args) {
    const store = ctx.store;
    if (store === null) return;
    await ctx.ui.openDialog(DIALOG.exportDialog, { projectId: store.projectId, quick: quickArg(args) });
  },
};

const PUBLISH_COMMANDS: readonly CommandDef[] = [
  exportCommand,
  {
    id: 'file.copyChartJson',
    title: m.cmdCopyChart,
    category: 'File',
    icon: 'ClipboardCopy',
    level: 'guided',
    aliases: ['copy chart', 'chart.json', 'copy json', 'clipboard', '复制谱面'],
    menus: [{ menu: 'app.project', group: 'file', order: 25 }],
    enabled: noProject,
    async run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      const prefs = readPrefs(ctx.settings);
      await runCopyChart(ctx, store, { options: currentOptions(store.doc, store.projectId, prefs), prefs });
    },
  },
  {
    id: 'publish.details',
    title: m.cmdDetails,
    category: 'Publish',
    icon: 'FilePen',
    level: 'guided',
    aliases: [
      'title',
      'song name',
      'level',
      'difficulty',
      'charter',
      'composer',
      'illustrator',
      'tags',
      'metadata',
      '谱面信息',
    ],
    enabled: noProject,
    run(ctx, args) {
      publishSession.requestFocus(focusFieldPath(fieldArg(args)));
      ctx.ui.showPanel(PUBLISH_PANELS.package);
    },
  },
  focusFieldCommand,
  {
    id: 'publish.playPreview',
    title: m.cmdPlayPreview,
    category: 'Publish',
    icon: 'Play',
    level: 'guided',
    aliases: ['preview', 'song select', 'previewStart', '预览'],
    enabled(ctx) {
      if (ctx.store === null) return { key: m.reasonNoProject };
      return ctx.audio.music !== null ? true : { key: m.previewNoSong };
    },
    async run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      const transport = ctx.audio.transport;
      const ms = await playPreviewRange(ctx, store.doc.meta, musicOffsetSec(store));
      const started = transport.state.startChartTime;
      setTimeout(() => {
        // Stop only this playback (not one the user started since).
        if (transport.state.playing && transport.state.startChartTime === started) transport.stop();
      }, ms);
    },
  },
  {
    id: 'publish.clampPreview',
    title: m.cmdClampPreview,
    category: 'Publish',
    icon: 'Wrench',
    level: 'guided',
    aliases: ['preview range', 'PH-36', 'previewEnd'],
    enabled(ctx) {
      const store = ctx.store;
      if (store === null) return { key: m.reasonNoProject };
      const song = songLengthSec(store.doc, ctx.audio.music);
      if (song === null) return { key: m.reasonNoSong };
      return clampedPreview(store.doc.meta, song) !== null ? true : { key: m.reasonPreviewOk };
    },
    run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      const fix = clampedPreview(store.doc.meta, songLengthSec(store.doc, ctx.audio.music));
      if (fix !== null) patchMeta(ctx, store, 'preview', fix, CLAMP_PREVIEW_LABEL);
    },
  },
];

/** Appendix C H6: the RPE checklist bundle of the open chart (full level; palette only). */
const RPE_CHECKLIST_COMMAND: CommandDef = {
  id: 'publish.rpeChecklist',
  title: m.cmdRpeChecklist,
  category: 'Publish',
  icon: 'ClipboardList',
  level: 'full',
  aliases: ['RPE checklist', 'verify RPE', 'RPE compatible', 'unverified'],
  enabled: noProject,
  async run(ctx) {
    const store = ctx.store;
    if (store === null) return;
    try {
      const bytes = await buildRpeChecklist(
        ctx,
        store,
        currentOptions(store.doc, store.projectId, readPrefs(ctx.settings)),
      );
      const name = `rpe-checklist-${store.doc.meta.rpeId || 'chart'}.zip`;
      downloadBytes(bytes, 'application/zip', name);
      ctx.ui.toast({ key: m.toastRpeChecklist, params: { name } }, { kind: 'success' });
    } catch (e) {
      ctx.ui.toast(
        { key: m.toastRpeChecklistFailed, params: { error: e instanceof Error ? e.message : String(e) } },
        { kind: 'error' },
      );
    }
  },
};

const EXPORT_KEY: KeyBinding = { chord: 'Ctrl+KeyE', command: 'file.export', args: { quick: true } };

export const PUBLISH_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: [EXPORT_KEY],
  rpe: [EXPORT_KEY],
};

const SETTINGS: readonly SettingDef[] = [
  {
    path: 'ext.publish.destination',
    title: m.settingDestination,
    description: m.settingDestinationHelp,
    level: 'guided',
    kind: 'custom',
    component: prefSelect('destination', m.settingDestination, [
      { value: 'download', label: m.destDownload },
      { value: 'saveAs', label: m.destSaveAs },
    ]),
  },
  {
    path: 'ext.publish.includeSidecar',
    title: m.settingSidecar,
    description: m.settingSidecarHelp,
    level: 'full',
    kind: 'custom',
    component: prefSwitch('includeSidecar', m.settingSidecar),
  },
  {
    path: 'ext.publish.includeInfoTxt',
    title: m.settingInfoTxt,
    level: 'full',
    kind: 'custom',
    component: prefSwitch('includeInfoTxt', m.settingInfoTxt),
  },
  {
    path: 'ext.publish.includeUnusedAssets',
    title: m.settingUnused,
    level: 'full',
    kind: 'custom',
    component: prefSwitch('includeUnusedAssets', m.settingUnused),
  },
  {
    path: 'ext.publish.pretty',
    title: m.settingPretty,
    level: 'full',
    kind: 'custom',
    component: prefSwitch('pretty', m.settingPretty),
  },
  {
    path: 'ext.publish.floatStyle',
    title: m.settingFloat,
    level: 'full',
    kind: 'custom',
    component: prefSelect('floatStyle', m.settingFloat, [
      { value: 'rpe', label: m.floatRpe },
      { value: 'shortest', label: m.floatShortest },
    ]),
  },
];

const HELP: readonly HelpTopicDef[] = [
  { id: 'publish', title: m.helpTitle, body: m.helpBody, level: 'guided', showMe: { panel: PUBLISH_PANELS.overview } },
];

export default {
  id: 'publish',
  commands: [...PUBLISH_COMMANDS, RPE_CHECKLIST_COMMAND],
  keybindings: PUBLISH_KEYBINDINGS,
  panels: [
    {
      id: PUBLISH_PANELS.overview,
      title: m.panelOverview,
      icon: 'ListChecks',
      slot: 'publish.left',
      order: 10,
      level: 'guided',
      component: PublishLeftPanel,
    },
    {
      id: PUBLISH_PANELS.package,
      title: m.panelPackage,
      icon: 'Package',
      slot: 'publish.right',
      order: 10,
      level: 'guided',
      component: PublishRightPanel,
    },
  ],
  dialogs: [{ id: DIALOG.exportDialog, kind: 'modal', component: ExportDialog }],
  settings: SETTINGS,
  helpTopics: HELP,
} satisfies FeatureModule;
